import { Block } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import Client from 'App/resources/Model/Client';
import Contact from 'App/resources/Model/Contact';
import DashboardButtonGroup from 'Components/Account/AccountDashboard/DashboardButtonGroup';
import DashboardPinnedComments from 'Components/Account/AccountDashboard/DashboardPinnedComments';
import DashboardAiSuggestions from 'Components/Account/AccountDashboard/DashboardAiSuggestions';
import ClientHistoryLog from 'App/components/HistoryLog/ClientHistoryLog';
import ContactHistoryLog from 'App/components/HistoryLog/ContactHistoryLog';
import React, { useCallback } from 'react';
import './Overview.scss';
import OpenActivityHistoryLog from 'App/components/HistoryLog/OpenActivityHistoryLog/OpenActivityHistoryLog';
import ActiveFlowHistoryLog from 'App/components/HistoryLog/ActiveFlowHistoryLog/ActiveFlowHistoryLog';
import { RootState } from 'Store/index';
import useSelector from 'App/components/hooks/useSelector/useSelector';
import { useMetadata } from 'App/components/hooks/appHooks';
import InactivationInfo from 'Components/Account/AccountDashboard/InactivationInfo/InactivationInfo';
import { shouldOpenModal } from 'App/services/Modal';
import openModal from 'App/services/Modal/Modal';
import UiElements from 'Components/UiElements';
import AppService from 'App/legacy/AppService';

type Props = {
	client: Client;
} & OptionalPropGroup<{
	isContact: true;
	// `Contact.userEditable` is a boolean, so the old `& { userEditable: true }`
	// here could not be satisfied by any Contact — the contact variant was only
	// reachable from the untyped Angular react-driver. Nothing below reads it.
	contact: Contact;
}>;

const ClientCardOverview = (props: Props) => {
	const { client, contact, isContact = false } = props;
	const notesFilterActive = useSelector((state: RootState) => state.NotesFilterActive);
	const metadata = useMetadata();
	const uiElementsConfig = metadata?.integrations?.uiElements?.account?.accountCardTop;

	const classes = new BemClass('ClientCardOverview');

	const editAccount = useCallback(() => {
		if (shouldOpenModal('EditClient')) {
			openModal('EditClient', { id: client.id });
		} else {
			const copiedAccount = structuredClone(client);

			// eslint-disable-next-line promise/catch-or-return
			Tools.$upModal.open('editAccount', {
				customerId: AppService.getCustomerId(),
				account: copiedAccount
			});
		}
	}, [client.id]);

	const renderedBy = props.isContact
		? ({ type: 'contact', id: props.contact.id } as const)
		: ({ type: 'company', id: client.id } as const);
	return (
		<Block className={classes.b()}>
			{!isContact && uiElementsConfig ? (
				<div className={classes.elem('widgets').b()}>
					<UiElements elements={uiElementsConfig} object={client} type="accountCardTop" />
				</div>
			) : null}
			{!isContact && !client.active ? (
				<InactivationInfo
					editAccount={editAccount}
					deactivatedBy={client.deactivatedBy}
					deactivationDate={client.deactivationDate}
					deactivationReason={client.deactivationReason}
				/>
			) : null}
			{client.active ? (
				<div className={classes.elem('comment').b()}>
					<DashboardButtonGroup
						isContact={isContact}
						account={client}
						contact={isContact ? contact : undefined}
						score={client.score}
					/>
				</div>
			) : null}

			<div className={classes.elem('aiSuggestions').b()}>
				<DashboardAiSuggestions clientId={client.id} />
			</div>
			<DashboardPinnedComments clientId={client.id} contactId={props.isContact ? props.contact.id : undefined} />
			<div className={classes.elem('timeline').b()}>
				<OpenActivityHistoryLog
					client={client}
					contact={isContact ? contact : undefined}
					isContactVariant={isContact}
					cardAdditionalProps={{ hasAvatar: true }}
				/>
				<ActiveFlowHistoryLog client={client} contact={isContact ? contact : undefined} />
				{props.isContact ? (
					<ContactHistoryLog
						contact={props.contact}
						client={client}
						notesFilterActive={notesFilterActive}
						renderedBy={renderedBy}
					/>
				) : (
					<ClientHistoryLog
						client={client}
						renderedBy={renderedBy}
						notesFilterActive={notesFilterActive}
						cardAdditionalProps={{ hasAvatar: true }}
					/>
				)}
			</div>
		</Block>
	);
};

export default ClientCardOverview;
